import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import JoditEditor from "jodit-react";
import { Toaster } from "modules/platform-components/Toaster";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { MultiSelect } from "react-multi-select-component";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AppContext } from "../../context/AppContext";
import {
  defaultDisableButtonsObject,
  FeedbackColumnDefs,
  status as questionStatuses,
  status_colors,
  WhereUsedColumnDefs
} from "../constants/QuestionBankConstants";
import {
  apiPaths,
  get,
  getData,
  postData,
  postInstituteAppData
} from "../platform-components/api-integration-util";
import { PreLoader } from "../platform-components/PreLoader";
import QuestionViewer from "../platform-components/question-viewer/QuestionViewer";
import {
  checkPermission,
  getEditQuestion
} from "../platform-components/utils/configuration";
import { getRoleNameList, getUserDetails } from "../platform-components/utils/shared-data.service";
import { hasPermission, getStoredPermissions } from "../platform-components/utils/permission.service";
import "./addquestion.scss";
import { answer, answerOptions } from "./constant";
import { getQuestionIds } from "./Dashboard-Service";

// ---------------------------------------------------------------------------
// Jodit editor configuration
//
// These MUST be stable module-scope objects. jodit-react keys the editor's whole
// lifecycle on the `config` prop's object IDENTITY:
//
//   useEffect(() => {
//     const jodit = Jodit.make(element, config);
//     return () => jodit.destruct();
//   }, [config, editorRef]);      // node_modules/jodit-react/src/JoditEditor.js
//
// Passing an inline object literal produced a new identity on every render, so
// every setState in this component - including setJoditValues, which the editors'
// own onBlur fires - destructed and rebuilt the editor. Two user-visible bugs came
// out of that:
//   1. Opening the image dialog blurs the editor, which re-rendered and destructed
//      the editor with the dialog still open, so the chosen file was never inserted.
//   2. On iPadOS, tapping Save blurred the editor; tearing down and rebuilding the
//      350px editor (plus the keyboard dismissing) reflowed the page between
//      touchstart and touchend, so iOS never synthesised the click.
//
// `uploader` must also be set explicitly. Jodit only renders the image dialog's
// "Upload" tab when `uploader.url || uploader.insertImageAsBase64URI` is truthy
// (jodit/src/modules/widget/file-selector/file-selector.ts). The option, explanation
// and reference editors previously received `config` imported from "process" - an
// IDE auto-import that resolves to undefined in the browser, since webpack's process
// shim has no `config` export - so they silently fell back to Jodit's defaults
// (insertImageAsBase64URI: false, url: "") and offered no Upload tab at all.
// ---------------------------------------------------------------------------

// Jodit's default imagesExtensions is ['jpg','png','jpeg','gif']; anything outside the
// list skips the base64 branch and falls through to a POST at uploader.url (empty),
// which fails silently. webp/bmp are added because they render everywhere. heic/heif
// are deliberately excluded - they would embed fine but only display in Safari - and
// so is svg, because an inlined SVG data URI can carry script and this HTML is
// rendered in the student apps.
const JODIT_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];

const JODIT_UPLOADER = {
  insertImageAsBase64URI: true,
  imagesExtensions: JODIT_IMAGE_EXTENSIONS,
};

const QUESTION_JODIT_CONFIG = {
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  readonly: false,
  enableDragAndDropFileToEditor: true,
  uploader: JODIT_UPLOADER,
  height: 350,
};

const QUESTION_JODIT_CONFIG_READONLY = {
  ...QUESTION_JODIT_CONFIG,
  readonly: true,
};

// Options / Explanation / Reference. No explicit height, so they keep the auto-sizing
// they had while running on Jodit's defaults.
const RICH_TEXT_JODIT_CONFIG = {
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  readonly: false,
  enableDragAndDropFileToEditor: true,
  uploader: JODIT_UPLOADER,
};

const RICH_TEXT_JODIT_CONFIG_READONLY = {
  ...RICH_TEXT_JODIT_CONFIG,
  readonly: true,
};

const AddQuestion = (props) => {
  let comments;
  const { QBEdit, qbId } = props;
  const routeParams = useParams();
  const routeParamId = routeParams?.id;
  let [id, setId] = useState(routeParamId || qbId);
  let [showWhereUsedGrid, setShowWhereUsedGrid] = useState(true);
  let [viewQuestion, setViewQuestion] = useState(QBEdit ? false : true);
  let [question, updateQuestion] = useState({});
  let [answers, setAnswers] = useState([]);
  let [edit, updateEdit] = useState(QBEdit || getEditQuestion());
  let [loading, updateLoading] = useState(true);
  let [displyNavs, setDisplayNavs] = useState(false);
  let [disablePrev, setDisablePrev] = useState(false);
  let [disableNext, setDisableNext] = useState(false);
  let [imagePreview, setImagePreview] = useState({ file: null });
  let [downloadMedia, setDownloadMedia] = useState({});
  let [questionData, setQuestionData] = useState({});
  let [selectedQuestonId, setWhereUsedQuestionId] = useState("");
  let [whereUsedPopupgridOptions, setwhereUsedPopupgridOptions] = useState({});
  let [feedbackPopupgridOptions, setFeedbackPopupgridOptions] = useState({});

  // Check if the query string contains 'qbpage=true'
  const hash = window.location.hash;
  const queryStringIndex = hash.indexOf("?");
  const queryString =
    queryStringIndex !== -1 ? hash.slice(queryStringIndex) : "";

  const queryParams = new URLSearchParams(queryString);
  const history = useHistory();

  const isQBPage = QBEdit || queryParams.get("qbpage") === "true";
  const addToTestId = queryParams.get("addToTest");
  const addToQBId = queryParams.get("addToQB");

  let [uploadMediaInformation, setUploadMediaInformation] = useState({});
  let {
    topics,
    courses,
    tags,
    subjects,
    subtopics,
    getTopics,
    getTags,
    getSubtopics,
  } = useContext(AppContext);
  const [availableTopics, setAvailableTopics] = useState([]);
  const editor = useRef(null);
  const [joditValues, setJoditValues] = useState({
    questionType: "EASY",
  });

  // Editor content is only pushed into React state on blur - deliberately, because
  // updating on every keystroke re-renders this whole component. That leaves a window
  // where Save runs before blur has committed and silently persists stale content
  // (the iPad tap-to-save case in particular). onChange mirrors the live HTML into
  // this ref instead, which costs no re-render, and onSave prefers it over joditValues.
  // Keyed by "question" | "explanation" | "reference" | "option<n>".
  const liveEditorValues = useRef({});
  const rememberEditorValue = (key) => (newContent) => {
    liveEditorValues.current[key] = newContent;
  };

  const [multiSelectValue, setMultiSelectValue] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [multiSelectTagValue, setMultiSelectTagValue] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [multiSelectCourseValue, setMultiSelectCourseValue] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);
  const [multiSelectTopicValue, setMultiSelectTopicValue] = useState([]);
  const [subtopicOptions, setSubtopicOptions] = useState([]);
  const [multiSelectSubtopicValue, setMultiSelectSubtopicValue] = useState([]);
  // Inline create-new modal for Topic / Subtopic / Tag. type is one of those three or null.
  const [inlineCreate, setInlineCreate] = useState({ open: false, type: null });
  const [inlineCreateValue, setInlineCreateValue] = useState("");
  const [inlineCreateSaving, setInlineCreateSaving] = useState(false);
  const [QBCount, setQBCount] = useState(0);
  const [nxtQuestion, setNxtQuestion] = useState(0);
  const [prevQuestion, setPrevQuestion] = useState(0);
  const isAdmin = getStoredPermissions()['Question Bank'] !== undefined
    ? hasPermission('Question Bank', 'View All Questions')
    : getRoleNameList().includes('Admin');
  let whereUsedGridApi = useRef();
  let feedbackGridApi = useRef();

  const {
    register,
    handleSubmit,
    watch,
    errors,
    setValue,
    getValues,
    formState,
    triggerValidation,
  } = useForm();

  useEffect(() => {
    if (props && props.examViewQuestionId) {
      setId(props.examViewQuestionId);
    }
  }, []);

  // Sync internal id when URL param or qbId prop changes from outside
  // (e.g., browser back/forward, or parent passing a new qbId).
  // Internal Next/Prev navigation uses setId directly and doesn't touch these,
  // so this effect never fights user navigation.
  useEffect(() => {
    const externalId = routeParamId || qbId;
    if (externalId != null && String(externalId) !== String(id)) {
      setId(externalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeParamId, qbId]);

  const downloadMediaFile = useCallback(() => {
    const responseType = "blob";
    return get(
      apiPaths.downloadMedia,
      {
        fileName: downloadMedia.mediaName,
        filePath: downloadMedia.mediaSrc,
      },
      {},
      responseType
    )
      .then((res) => {
        setDownloadMedia({});
        setImagePreview({ file: URL.createObjectURL(res) });
      })
      .catch((e) => {
        setDownloadMedia({});
        console.log(e);
      });
  }, [downloadMedia.mediaName, downloadMedia.mediaSrc]);

  useEffect(() => {
    if (Object.keys(downloadMedia)?.length > 0) {
      downloadMediaFile(downloadMedia);
    }
  }, [downloadMedia, downloadMediaFile]);

  useEffect(() => {
    if (+id !== 0) {
      // Previous/Next navigation for the QuestionViewer is derived from the ids of the
      // page the user navigated from (captured in Dashboard-Service by both the Dashboard
      // and Question Bank pages), instead of refetching the entire question bank
      // (POST /questionslist) on every view.
      //
      // The old code fetched the list ordered by question_id ASC and picked neighbours
      // by index (prev = index-1 = older, next = index+1 = newer, count = last = max id).
      // We reproduce those exact semantics here, sorting ascending so we are independent
      // of how the source page ordered its ids (Dashboard = DESC, Question Bank = ASC).
      const ids = [...getQuestionIds()].map(Number).sort((a, b) => a - b);
      const index = ids.indexOf(+id);
      setQBCount(ids.length > 0 ? ids[ids.length - 1] : 0);
      setPrevQuestion(index > 0 ? ids[index - 1] : null);
      setNxtQuestion(
        index >= 0 && index < ids.length - 1 ? ids[index + 1] : null
      );
    }
  }, [id]);

  useEffect(() => {
    onWhereUsedButtonClick(+id);
  }, [id]);

  useEffect(() => {
    if (tags) {
      setTagOptions(
        tags.map((tag) => {
          return {
            label: tag.tagName,
            value: tag.tagId,
          };
        })
      );
    }
  }, [tags]);

  useEffect(() => {
    setTopicOptions(
      availableTopics.map((topic) => {
        return {
          label: topic.topic,
          value: topic.topicId,
        };
      })
    );
  }, [availableTopics, availableTopics.length]);

  // Subtopics available for selection = every subtopic whose parent topic is currently selected.
  // This only rebuilds the option list; it never deselects an already-chosen subtopic (pruning on
  // topic change is handled explicitly in onTopicChange so that edit-hydration cannot lose data).
  useEffect(() => {
    const selectedTopicIds = (multiSelectTopicValue || []).map((t) => t.value);
    setSubtopicOptions(
      (subtopics || [])
        .filter((s) => selectedTopicIds.includes(s.topicId))
        .map((s) => {
          return {
            label: s.subtopicName,
            value: s.subtopicId,
          };
        })
    );
  }, [subtopics, multiSelectTopicValue]);

  // Wraps the Topic multi-select onChange so a USER-initiated topic change drops any selected
  // subtopics that no longer belong to a selected topic. frameObject() sets the topic value directly
  // (not through this handler), so hydrating an existing question never triggers pruning.
  const onTopicChange = (selected) => {
    setMultiSelectTopicValue(selected);
    const selectedTopicIds = (selected || []).map((t) => t.value);
    setMultiSelectSubtopicValue((prev) =>
      (prev || []).filter((sel) => {
        const st = (subtopics || []).find((s) => s.subtopicId === sel.value);
        // Only drop a subtopic we can POSITIVELY confirm belongs to a now-deselected topic.
        // If the subtopics catalog hasn't loaded (or the record isn't found), keep the selection
        // rather than wiping valid data based on incomplete context.
        return !st || selectedTopicIds.includes(st.topicId);
      })
    );
  };

  let onWhereUsedButtonClick = (wQid) => {
    if (!edit) {
      if (wQid && +wQid !== 0) {
        setShowWhereUsedGrid(false);
        setWhereUsedQuestionId(wQid);
        get(`${apiPaths.getQuestionHistory}/${wQid}`, {
          tenantId: getUserDetails().tenantId,
        }).then((res) => {
          plotWhereUsedGrid(res?.result);
        });
        get(
          `${apiPaths.getQuestionFeedbackById}?libTypeList=TEST&libTypeList=QBANK&questionId=${wQid}`,
          {
            tenantId: getUserDetails().tenantId,
          }
        ).then((res) => {
          plotFeedbackGrid(res?.result);
        });
      }
    }
  };

  const plotWhereUsedGrid = (rowData) => {
    const whereUsedPopupgridOptions = {};
    whereUsedPopupgridOptions.pagination = true;
    whereUsedPopupgridOptions.columnDefs = WhereUsedColumnDefs;
    whereUsedPopupgridOptions.rowData = rowData;

    setTimeout(() => whereUsedGridApi?.current?.setRowData(rowData));

    setwhereUsedPopupgridOptions(whereUsedPopupgridOptions);
    setShowWhereUsedGrid(true);
  };

  const plotFeedbackGrid = (rowData) => {
    const feedbackPopupgridOptions = {};
    feedbackPopupgridOptions.pagination = true;
    feedbackPopupgridOptions.columnDefs = FeedbackColumnDefs;
    feedbackPopupgridOptions.rowData = rowData;

    setTimeout(() => feedbackGridApi?.current?.setRowData(rowData));

    setFeedbackPopupgridOptions(feedbackPopupgridOptions);
  };

  useEffect(() => {
    if (courses) {
      setCourseOptions(
        courses.map((course) => {
          return {
            label: course.course,
            value: course.courseId,
          };
        })
      );
    }
  }, [courses]);

  let [displayComments, setDisplayComments] = useState(false);
  let status = useRef();

  let [disableButtons, setDisableButtons] = useState(
    JSON.parse(JSON.stringify(defaultDisableButtonsObject))
  );

  const isValidUpdate = (rows) => {
    if (!rows) {
      return;
    }
    Object.keys(questionStatuses).forEach((qs) => {
      switch (questionStatuses[qs]) {
        case questionStatuses.SENT_FOR_APPROVAL:
          let invalidRowIds1 =
            rows.questionStatus == questionStatuses.SENT_FOR_APPROVAL;
          let Message1 = "Question which is already in Sent For Approval state";
          setDisableButtons((prev) => {
            let a = {};
            a[questionStatuses[qs]] = prev[questionStatuses[qs]];
            a[questionStatuses[qs]]["disable"] = invalidRowIds1 != 0;
            a[questionStatuses[qs]]["Message"] = a[questionStatuses[qs]][
              "disable"
            ]
              ? Message1
              : "";
            return { ...prev, ...a };
          });
          break;
        case questionStatuses.APPROVED:
        case questionStatuses.CORRECTION_NEEDED:
        case questionStatuses.REJECTED:
          let invalidRowIds2 =
            rows.questionStatus !== questionStatuses.SENT_FOR_APPROVAL;
          let Message2 = "Question is not yet Sent For Approval ";
          setDisableButtons((prev) => {
            let a = {};
            a[questionStatuses[qs]] = prev[questionStatuses[qs]];
            a[questionStatuses[qs]]["disable"] = invalidRowIds2 != 0;
            a[questionStatuses[qs]]["Message"] = a[questionStatuses[qs]][
              "disable"
            ]
              ? Message2
              : "";
            return { ...prev, ...a };
          });
          break;
        default:
          return;
      }
    });
  };

  const onClickCommentsOk = () => {
    // Capture the pending transition up front - status.current is a mutable ref and the
    // request below is async, so reading it again later could pick up a different value.
    const newStatus = status.current;
    const data = {
      comment: JSON.stringify(comments),
      newStatus,
      questionId: [id],
    };

    comments = "";
    status.current = null;
    setDisplayComments(false);

    // The comments modal must only ever be opened with a pending status transition.
    // Posting a null newStatus makes the backend insert a null question_status_audit
    // row, which fails with "Column 'new_status' cannot be null".
    if (!newStatus) {
      return;
    }

    postData(apiPaths.updateQuestionStatus, {
      data,
      params: {
        tenantId: getUserDetails()?.tenantId,
        userEmail: getUserDetails()?.email,
      },
    })
      .then((val) => {
        updateQuestion({ ...question, questionStatus: newStatus });
        isValidUpdate({ ...question, questionStatus: newStatus });
        // The status transition lands after the save-triggered refetch, so ask the
        // host to refetch again - otherwise the viewer shows the pre-transition status.
        if (props.onSaved) {
          props.onSaved(+id);
        }
        // if (questionStatuses.APPROVED === status.current) {
        //   updateEdit(false);
        //   //editor.setReadOnly(true);
        // }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // Statuses that must persist the current edits before the status change,
  // so unsaved form changes are not silently dropped by the status-only endpoint.
  const saveFirstStatuses = [
    questionStatuses.APPROVED,
    questionStatuses.SENT_FOR_APPROVAL,
  ];

  const updateQuestionStatus = (val) => {
    if (isQBPage && saveFirstStatuses.includes(val) && edit && !viewQuestion) {
      // Save edits first, then apply the status change — only when actively editing
      onSave("SAVED", val);
    } else {
      setDisplayComments(true);
      status.current = val;
    }
  };

  const frameObject = (result) => {
    if (result)

      isValidUpdate(result);
    updateLoading(false);

    if (!result?.questionId) {
      // Deep copy - see the note on the other setAnswers(answer) call site.
      setAnswers(JSON.parse(JSON.stringify(answer)));
    }
    if (result?.questionId) {
      updateQuestion(result);
      setTimeout(() => {
        for (let key in result) {
          if (key === "question_media_map") {
            if (result[key] instanceof Array && result[key]?.length > 0) {
              let mediaInfo = {};
              result[key].forEach((mediaDetails) => {
                if (mediaDetails?.mediaInfo?.mediaType === "IMAGE") {
                  mediaInfo = mediaDetails?.mediaInfo;
                }
              });
              setDownloadMedia(mediaInfo);
              setUploadMediaInformation(mediaInfo);
            } else {
              setUploadMediaInformation({});
            }
          }
          if (key === "question_course_map") {
            const selectedCourses = result?.[key]?.map((course) => {
              return {
                label: course.course,
                value: course.courseId,
              };
            });
            setMultiSelectCourseValue(selectedCourses);
            setValue(key, selectedCourses);
            continue;
          }
          if (key === "question_topic_map") {
            let topics = result?.[key];
            const selectedTopics = topics?.map((topic) => {
              return {
                label: topic.topic,
                value: topic.topicId,
              };
            });
            setMultiSelectTopicValue(selectedTopics);
            setValue(key, selectedTopics);
            continue;
          }

          if (key === "question_subtopic_map") {
            let subtopicsList = result?.[key];
            const selectedSubtopics = subtopicsList?.map((subtopic) => {
              return {
                label: subtopic.subtopicName,
                value: subtopic.subtopicId,
              };
            });
            setMultiSelectSubtopicValue(selectedSubtopics);
            setValue(key, selectedSubtopics);
            continue;
          }

          if (key === "question_tag_map") {
            let tags = result?.[key];
            const selectedTags = tags?.map((tag) => {
              return {
                label: tag.tagName,
                value: tag.tagId,
              };
            });
            setMultiSelectTagValue(selectedTags);
            setValue(key, selectedTags);
            continue;
          }

          if (key === "topics") {
            let topics = result?.[key];
            topics.forEach((ele, index) => {
              setValue("topics" + index, ele?.topicName);
            });
            continue;
          }
          if (key === "answers") {
            result[key] = (result?.[key] || []).sort(
              (a, b) => a?.answerOrder - b?.answerOrder
            );
            setAnswers(result?.[key]);
            let answers = result?.[key];
            let currentJoditValues = { ...joditValues };
            answers.forEach((ele, index) => {
              currentJoditValues["option" + (index + 1)] = ele?.answer;
            });

            setJoditValues({
              ...joditValues,
              ...currentJoditValues,
              subjectId: result.subjectId,
              ...result,
            });
            continue;
          }
          if (key === "answerType") {
            let answers = result?.answers;
            let correctAnswerIds = (answers || [])
              .filter((i) => i.correct)
              .map((ele) => ele.ansOrder + 1);
            let correctOptionsList = answerOptions.filter((i) =>
              correctAnswerIds.includes(i.answerId)
            );
            setMultiSelectValue(correctOptionsList);
            setValue(key, result[key]);
            continue;
          }
          if (
            key === "question" ||
            key === "explanation" ||
            key === "reference"
          ) {
            setJoditValues({ ...joditValues, [key]: result[key] });
            continue;
            //getMultiLineString(result[key]);
          }
          setValue(key, result[key]);
        }
      });
      setAnswers(result.answers);
    }
  };

  useEffect(() => {
    // Loading a different question (or toggling View/Edit, which refetches) replaces
    // every editor's content, so drop the live cache - otherwise the previous
    // question's text could be carried into the next save.
    liveEditorValues.current = {};

    if (+id !== 0) {
      if (id != 0 && getQuestionIds().length > 1) {
        onNavClick("p", true);
        setDisplayNavs(true);
      }
      get(apiPaths.questionbyid, { id })
        .then(({ result }) => {
          console.log("result: ", result);
          frameObject(result);
          setQuestionData(result);
        })
        .catch((val) => frameObject(null));
    } else {
      // `answer` is a shared module-level array (export let answer = [...] in
      // ./constant). onBlur below copies the array but not its objects, so writing
      // ans[ind].answer mutated that shared template and pre-filled the next
      // "Add Question" with the previous question's options. Seed from a deep copy.
      setAnswers(JSON.parse(JSON.stringify(answer)));
      updateLoading(false);
    }
  }, [id, edit, viewQuestion]);

  const displayError = (msg) => <span>{msg}</span>;

  const inlineCreateLabels = {
    topic: "Topic",
    subtopic: "Subtopic",
    tag: "Tag",
  };

  const openInlineCreate = (type) => {
    if (!edit) return;
    if (type === "topic" && !watch("subjectId")) {
      Toaster(
        toast.TYPE.WARNING,
        "Please select a Subject before adding a new Topic."
      );
      return;
    }
    if (type === "subtopic") {
      const selectedTopicIds = (multiSelectTopicValue || []).map((t) => t.value);
      if (selectedTopicIds.length !== 1) {
        Toaster(
          toast.TYPE.WARNING,
          "Please select exactly one Topic before adding a new Subtopic."
        );
        return;
      }
    }
    setInlineCreateValue("");
    setInlineCreate({ open: true, type });
  };

  const closeInlineCreate = () => {
    if (inlineCreateSaving) return;
    setInlineCreate({ open: false, type: null });
    setInlineCreateValue("");
  };

  const selectOptionOnce = (setter, option) => {
    setter((prev) => [
      ...(prev || []).filter((p) => p.value !== option.value),
      option,
    ]);
  };

  const handleInlineCreateSave = async () => {
    const type = inlineCreate.type;
    const value = (inlineCreateValue || "").trim();
    if (!value) {
      Toaster(toast.TYPE.WARNING, "Please enter a name.");
      return;
    }
    setInlineCreateSaving(true);
    try {
      if (type === "tag") {
        // Create-if-not-present: saveTag does not dedup server-side, so avoid duplicates here.
        const existing = (tags || []).find(
          (t) => (t.tagName || "").toLowerCase() === value.toLowerCase()
        );
        if (existing) {
          selectOptionOnce(setMultiSelectTagValue, {
            label: existing.tagName,
            value: existing.tagId,
          });
          Toaster(toast.TYPE.INFO, "Tag already exists — selected it.");
        } else {
          await postData(apiPaths.saveTag, { data: { tagName: value } });
          const fresh = await getData(apiPaths.tags, { data: {} });
          const list = fresh?.result || [];
          if (getTags) getTags();
          const created = list.find(
            (t) => (t.tagName || "").toLowerCase() === value.toLowerCase()
          );
          if (created) {
            selectOptionOnce(setMultiSelectTagValue, {
              label: created.tagName,
              value: created.tagId,
            });
          }
          Toaster(toast.TYPE.SUCCESS, "Tag added.");
        }
      } else if (type === "topic") {
        const subjectId = +watch("subjectId");
        // Create-if-not-present: saveTopic does not dedup server-side.
        const existing = (topics || []).find(
          (t) =>
            (t.topic || "").toLowerCase() === value.toLowerCase() &&
            t.subjectId === subjectId
        );
        if (existing) {
          selectOptionOnce(setMultiSelectTopicValue, {
            label: existing.topic,
            value: existing.topicId,
          });
          Toaster(toast.TYPE.INFO, "Topic already exists — selected it.");
        } else {
          await postData(apiPaths.saveTopic, {
            data: { topic: value, subjectId },
          });
          const fresh = await getData(apiPaths.topics, { data: {} });
          const list = fresh?.result || [];
          if (getTopics) getTopics();
          const created = list.find(
            (t) =>
              (t.topic || "").toLowerCase() === value.toLowerCase() &&
              t.subjectId === subjectId
          );
          if (created) {
            selectOptionOnce(setMultiSelectTopicValue, {
              label: created.topic,
              value: created.topicId,
            });
          }
          Toaster(toast.TYPE.SUCCESS, "Topic added.");
        }
      } else if (type === "subtopic") {
        const topicId = (multiSelectTopicValue || [])[0]?.value;
        // saveSubtopic dedups case-insensitively server-side and returns the existing/created row.
        const res = await postData(apiPaths.saveSubtopic, {
          data: { topicId, subtopicName: value },
        });
        const created = res?.result;
        if (getSubtopics) getSubtopics();
        if (created && created.subtopicId) {
          selectOptionOnce(setMultiSelectSubtopicValue, {
            label: created.subtopicName,
            value: created.subtopicId,
          });
          Toaster(toast.TYPE.SUCCESS, "Subtopic added.");
        } else {
          Toaster(toast.TYPE.WARNING, "Could not add subtopic.");
        }
      }
      setInlineCreate({ open: false, type: null });
      setInlineCreateValue("");
    } catch (e) {
      Toaster(
        toast.TYPE.ERROR,
        typeof e === "string" ? e : "Could not save. Please try again."
      );
    } finally {
      setInlineCreateSaving(false);
    }
  };

  useEffect(() => {
    if (watch("subjectId")) {
      setAvailableTopics(
        topics.filter((topic) => topic.subjectId === +watch("subjectId"))
      );
    }
  }, [getValues, watch("subjectId"), topics, topics.length]);

  const onSave = async (questionStatus, val = null) => {
    if (questionStatus === "CLOSE" && !isQBPage) {
      if (addToTestId) {
        props.history.push("/app/tests");
      } else if (addToQBId) {
        props.history.push("/app/qb");
      } else {
        props.history.push("/app/dashboard");
      }
      return;
    }
    // Use triggerValidation's own result. The previous guard read `errors` from the
    // render closure, i.e. the value from BEFORE this validation run, and returned
    // with no toast and no message - which is indistinguishable from "the Save button
    // did nothing". (Only subjectId is actually registered: the MultiSelect refs are
    // no-ops because react-multi-select-component is not a forwardRef component.)
    const isValid = await triggerValidation();
    if (!isValid) {
      Toaster(
        toast.TYPE.WARNING,
        "Please complete the required fields before saving."
      );
      return;
    }
    let data = {
      ...watch(),
      tenantId: getUserDetails()?.tenantId,
      questionStatus,
      createdBy: getUserDetails().email || "",
      updatedBy: getUserDetails().email || "",
      createdDate: question?.createdDate,
    };
    data.subjectId = +(data.subjectId || question?.subjectId);
    // Prefer the live editor content over the last blurred value, so a Save that lands
    // before blur has committed still persists what the user actually typed.
    const liveOr = (key, fallback) =>
      liveEditorValues.current[key] !== undefined
        ? liveEditorValues.current[key]
        : fallback;
    data.question = liveOr("question", joditValues?.question);
    data.explanation = liveOr("explanation", joditValues?.explanation);
    data.reference = liveOr("reference", joditValues?.reference);
    let values = { ...watch(), ...joditValues };
    data.answerType = multiSelectValue.length === 1 ? "Single" : "Multi";
    data.question_course_map = multiSelectCourseValue.map((el) => {
      return {
        courseId: el.value,
      };
    });
    data.question_tag_map = multiSelectTagValue.map((el) => {
      return {
        tagId: el.value,
      };
    });
    data.question_topic_map = multiSelectTopicValue.map((el) => {
      return {
        topicId: el.value,
      };
    });
    data.question_subtopic_map = (multiSelectSubtopicValue || []).map((el) => {
      return {
        subtopicId: el.value,
      };
    });
    data.mediaId = uploadMediaInformation?.mediaId || 0;
    data.topics = [];
    data.answers = answers;
    Object.getOwnPropertyNames(joditValues).forEach((key) => {
      if (key.indexOf("option") !== -1) {
        let index = +key.substr(6, key.length) - 1;
        answers[index].correct = multiSelectValue
          .map((i) => i.answerId)
          .includes(answers[index]?.answerId);
      }
    });
    data.tenantId = getUserDetails().tenantId;
    Object.getOwnPropertyNames(values).forEach((key) => {
      if (key.indexOf("topics") !== -1) {
        let index = +key.substr(6, key.length);
        topics[index].topicName = values[key];
        data.topics.push(topics[index]);
      }
    }, []);
    data.answers = answers.map((el, idx) => {
      return {
        ...el,
        answer: liveOr("option" + (idx + 1), el.answer),
        correct: multiSelectValue.some((td) => td.value.includes(idx + 1)),
      };
    });
    if (id !== 0) {
      data["questionId"] = +id;
    }
    data.subject = subjects.filter(
      (el) => el.subjectId === +data.subjectId
    )?.[0]?.subject;
    data.questionType = joditValues?.questionType ?? "EASY";

    // questions.question / answer_explanation / reference and answers.answer are all
    // MEDIUMTEXT (16 MB), so the column is no longer the binding limit. What still
    // bites is that the editor inlines images as base64 data URIs: a large photo
    // makes every read of this question (test render, QBank fetch, AI prompt) carry
    // megabytes of payload, and a big enough statement trips MySQL's
    // max_allowed_packet as an opaque "Error occured, ...". Cap well below both.
    const FIELD_SIZE_LIMIT = 5 * 1024 * 1024;
    const byteLength = (s) => (s ? new Blob([s]).size : 0);
    const oversizedFields = [
      ["Question", byteLength(data.question)],
      ["Explanation", byteLength(data.explanation)],
      ["Reference", byteLength(data.reference)],
      ...(data.answers || []).map((a, i) => [
        "Option " + (i + 1),
        byteLength(a?.answer),
      ]),
    ].filter(([, size]) => size > FIELD_SIZE_LIMIT);

    if (oversizedFields.length > 0) {
      Toaster(
        toast.TYPE.ERROR,
        `${oversizedFields
          .map(([field]) => field)
          .join(", ")} ${
          oversizedFields.length === 1 ? "is" : "are"
        } too large to save. Images are stored inline with the text, so please insert a smaller or compressed image.`
      );
      return;
    }

    postData(apiPaths.question, {
      data,
    })
      .then(async (res) => {
        const newQuestionId = res.result;

        // Auto-add to Test if navigated from Tests page
        if (addToTestId && newQuestionId) {
          try {
            const addRes = await postInstituteAppData(apiPaths.addQuestionToTest, {
              data: [],
              params: {
                questionId: newQuestionId,
                libraryTestId: addToTestId
              },
              headers: {
                tenantId: getUserDetails().tenantId,
                userEmail: getUserDetails().email,
              },
            });
            if (addRes.result == true) {
              Toaster(toast.TYPE.SUCCESS, `Question ${newQuestionId} created and added to Test`);
            } else {
              Toaster(toast.TYPE.WARNING, `Question ${newQuestionId} created but failed to add to Test`);
            }
          } catch (e) {
            Toaster(toast.TYPE.WARNING, `Question ${newQuestionId} created but failed to add to Test`);
          }
          props.history.push("/app/tests");
          return;
        }

        // Auto-add to QB if navigated from Question Bank page
        if (addToQBId && newQuestionId) {
          try {
            const addRes = await postInstituteAppData(apiPaths.addQuestionToQB, {
              data: [],
              params: {
                questionId: newQuestionId,
                libraryQBModuleId: addToQBId
              },
              headers: {
                tenantId: getUserDetails().tenantId,
                userEmail: getUserDetails().email,
              },
            });
            if (addRes.result == true) {
              Toaster(toast.TYPE.SUCCESS, `Question ${newQuestionId} created and added to Question Bank`);
            } else {
              Toaster(toast.TYPE.WARNING, `Question ${newQuestionId} created but failed to add to Question Bank`);
            }
          } catch (e) {
            Toaster(toast.TYPE.WARNING, `Question ${newQuestionId} created but failed to add to Question Bank`);
          }
          props.history.push("/app/qb");
          return;
        }

        if (!isQBPage) {
          props.history.push(`/app/question/${res.result}`);
          window.location.reload();
          return;
        }

        // Tell the host that the persisted copy changed. The Question Bank modal
        // renders this component for editing but its OWN QuestionViewer for viewing,
        // fed by questionData that QuestionBank fetched before the edit started.
        // Nothing there is keyed on the save, so exiting edit mode re-rendered the
        // pre-edit content until a full page refresh.
        // Guarded call, not optional chaining: a standalone `props.onSaved?.()`
        // statement trips no-unused-expressions on this project's react-scripts.
        if (props.onSaved) {
          props.onSaved(+id || res.result);
        }

        if (val) {
          // Save-first flow: the edits are persisted, now collect the audit comment and
          // apply the requested status transition via the status endpoint.
          status.current = val;
          setDisplayComments(true);
        } else {
          // Plain Save on the Question Bank page - there is no status transition to
          // apply, so the comments modal must not open (it would post newStatus: null).
          // The save above already persisted question_status, so mirror it locally to
          // keep the status buttons in sync with what is now in the DB.
          const savedQuestion = { ...question, questionStatus };
          updateQuestion(savedQuestion);
          isValidUpdate(savedQuestion);
          Toaster(toast.TYPE.SUCCESS, "Question saved");
        }
      })
      .catch((e) => Toaster(toast.TYPE.ERROR, `Error occured, ${e}`));
  };

  const onNavClick = (button, initial) => {
    let ids = getQuestionIds();
    let index = ids.indexOf(+id);

    if (!initial) {
      if (button === "prev") {
        index--;
      } else {
        index++;
      }
    }
    if (index == 0) {
      if (!disablePrev) setDisablePrev(true);
    } else {
      if (disablePrev) setDisablePrev(false);
    }
    if (index === ids.length - 1 || ids.length === 1) {
      if (!disableNext) setDisableNext(true);
    } else {
      if (disableNext) setDisableNext(false);
    }
    // props.history.push('/app/question/' + ids[index + 1]);
    if (!initial) setId(ids[index]);
  };
  const onsub = () => {
    if (process && process.exit) process.exit(1);
  };

  const back = () => {
    // Navigate back to the previous route while preserving the route state
    history.goBack();
  };

  return (
    <div style={{ display: "flex", width: "100%", flexFlow: "column" }}>
      {loading ? (
        <div>
          <PreLoader />
        </div>
      ) : (
        <div className="addquestion-main">
          {(isQBPage && !QBEdit || addToTestId || addToQBId) && (
            <Button
              variant="outlined"
              onClick={back}
              style={{ marginTop: "10px", marginBottom: "10px" }}
            >
              Back
            </Button>
          )}
          <div
            className="main-heading"
            style={{
              width: "100%",
              display: "flex",
              marginBottom: "10px",
              justifyContent: "space-between",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {!edit ? "View" : +id === 0 ? "Add" : viewQuestion ? "View" : "Edit"} Question
              {question?.questionStatus && (() => {
                const colorKey = Object.keys(questionStatuses).find(
                  (key) => questionStatuses[key] === question.questionStatus
                );
                const bgColor = status_colors[colorKey] || "grey";
                return (
                  <Chip
                    label={question.questionStatus}
                    size="small"
                    style={{
                      backgroundColor: bgColor,
                      color: ["yellow", "#FFB347"].includes(bgColor) ? "#333" : "#fff",
                      fontWeight: "bold",
                    }}
                  />
                );
              })()}
            </span>
            {edit && +id > 0 && questionData?.questionId && (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  style={{ padding: "0 5px" }}
                  variant="outlined"
                  onClick={() => {
                    // In QuestionViewModal's edit flow, clicking "View" should
                    // exit edit mode entirely so the user returns to the outer
                    // modal's QuestionViewer (with rowData-based prev/next),
                    // not AddQuestion's internal viewer which has its own
                    // status-filtered list and often disables Next/Prev.
                    if (QBEdit && !viewQuestion && props.onExitEdit) {
                      props.onExitEdit();
                    } else {
                      setViewQuestion(!viewQuestion);
                    }
                  }}
                >
                  {viewQuestion ? "Edit" : "View"}
                </Button>
                {viewQuestion &&
                  questionData?.questionId && (
                    <Button
                      style={{ marginLeft: "5px" }}
                      data-toggle="modal"
                      data-target="#myModal"
                      onClick={() =>
                        updateQuestionStatus(questionStatuses.APPROVED)
                      }
                      disabled={
                        !isAdmin && disableButtons[questionStatuses.APPROVED].disable
                      }
                      title={disableButtons[questionStatuses.APPROVED].Message}
                    >
                      Approve
                    </Button>
                  )}
                {viewQuestion &&
                  questionData?.questionId && (
                    <Button
                      style={{ marginLeft: "5px" }}
                      onClick={() =>
                        updateQuestionStatus(questionStatuses.CORRECTION_NEEDED)
                      }
                      disabled={
                        !isAdmin && disableButtons[questionStatuses.CORRECTION_NEEDED]
                          .disable
                      }
                      title={
                        disableButtons[questionStatuses.CORRECTION_NEEDED]
                          .Message
                      }
                      data-toggle="modal"
                      data-target="#myModal"
                    >
                      Correction Needed
                    </Button>
                  )}
                {viewQuestion &&
                  questionData?.questionId && (
                    <Button
                      style={{ marginLeft: "5px" }}
                      onClick={() =>
                        updateQuestionStatus(questionStatuses.REJECTED)
                      }
                      disabled={
                        !isAdmin && disableButtons[questionStatuses.REJECTED].disable
                      }
                      title={disableButtons[questionStatuses.REJECTED].Message}
                      data-toggle="modal"
                      data-target="#myModal"
                    >
                      Reject
                    </Button>
                  )}
              </div>
            )}
          </div>
          {edit && (!viewQuestion || +id === 0) && (
            <div>
              <div className="background-common-div">
                <div className="addquestion-sub">
                  <form onSubmit={handleSubmit(onsub)} className="form-div">
                    <div className="btn-div" style={{ float: "right" }}>
                      {edit ? (
                        <React.Fragment>
                          {/* type="button" on every action button: without it these
                              default to type="submit" inside the <form> above, so each
                              tap fired both the onClick handler AND the form's
                              handleSubmit(onsub). */}
                          <button
                            type="button"
                            className="btn-common"
                            onClick={() => onSave("SAVED")}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="btn-common"
                            {...(!(isQBPage && edit && !viewQuestion) && {
                              "data-toggle": "modal",
                              "data-target": "#myModal",
                            })}
                            onClick={() =>
                              updateQuestionStatus(
                                questionStatuses.SENT_FOR_APPROVAL
                              )
                            }
                            disabled={
                              disableButtons[
                                questionStatuses.SENT_FOR_APPROVAL
                              ].disable
                            }
                            title={
                              disableButtons[
                                questionStatuses.SENT_FOR_APPROVAL
                              ].Message
                            }
                          >
                            Send for Approval
                          </button>
                          <button
                            type="button"
                            className="btn-common"
                            {...(!(isQBPage && edit && !viewQuestion) && {
                              "data-toggle": "modal",
                              "data-target": "#myModal",
                            })}
                            onClick={() =>
                              updateQuestionStatus(questionStatuses.APPROVED)
                            }
                            disabled={
                              disableButtons[questionStatuses.APPROVED]
                                .disable && !isQBPage
                            }
                            title={
                              disableButtons[questionStatuses.APPROVED]
                                .Message
                            }
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="btn-common"
                            onClick={() =>
                              updateQuestionStatus(
                                questionStatuses.CORRECTION_NEEDED
                              )
                            }
                            disabled={
                              disableButtons[
                                questionStatuses.CORRECTION_NEEDED
                              ].disable
                            }
                            title={
                              disableButtons[
                                questionStatuses.CORRECTION_NEEDED
                              ].Message
                            }
                            data-toggle="modal"
                            data-target="#myModal"
                          >
                            Correction Needed
                          </button>
                          <button
                            type="button"
                            className="btn-common"
                            onClick={() =>
                              updateQuestionStatus(questionStatuses.REJECTED)
                            }
                            disabled={
                              disableButtons[questionStatuses.REJECTED]
                                .disable
                            }
                            title={
                              disableButtons[questionStatuses.REJECTED]
                                .Message
                            }
                            data-toggle="modal"
                            data-target="#myModal"
                          >
                            Reject
                          </button>
                        </React.Fragment>
                      ) : (
                        ""
                      )}
                      {props.examViewQuestionId === undefined && !isQBPage && (
                        <div class="close-btn" onClick={() => onSave("CLOSE")}>
                          <i class="fa fa-close" aria-hidden="true"></i>
                        </div>
                      )}
                    </div>

                    <div className="common-div">
                      <div className="row">
                        <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6">
                          <div className="form-sub-div">
                            <h6 className="text-bold">Subject</h6>
                            <div className="form-elements">
                              {edit ? (
                                <select
                                  className="form-details questions"
                                  name="subjectId"
                                  ref={register({ required: true })}
                                  onChange={(e) => {
                                    setJoditValues({
                                      ...joditValues,
                                      subjectId: e.target.value,
                                    });
                                  }}
                                  readOnly={!edit}
                                >
                                  {subjects.map((ele) => (
                                    <option
                                      key={ele?.subjectId}
                                      value={ele?.subjectId}
                                    >
                                      {ele?.subject}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  className="form-details"
                                  value={question.subject}
                                  readOnly
                                />
                              )}
                            </div>
                          </div>

                          <div className="form-sub-div">
                            <h6 className="text-bold">Courses</h6>
                            <div className="form-elements">
                              <MultiSelect
                                disabled={!edit}
                                ref={register({ required: true })}
                                options={courseOptions} // Options to display in the dropdown
                                value={multiSelectCourseValue} // Preselected value to persist in dropdown
                                onChange={setMultiSelectCourseValue} // Function will trigger on select event
                                labelledBy={"Please Select"}
                                hasSelectAll={true}
                              />
                            </div>
                          </div>
                          <div className="form-sub-div">
                            <h6 className="text-bold">
                              Tags
                              {edit && (
                                <span
                                  className="inline-add-link"
                                  onClick={() => openInlineCreate("tag")}
                                >
                                  + New
                                </span>
                              )}
                            </h6>
                            <div className="form-elements">
                              <MultiSelect
                                disabled={!edit}
                                ref={register({ required: true })}
                                options={tagOptions} // Options to display in the dropdown
                                value={multiSelectTagValue} // Preselected value to persist in dropdown
                                onChange={setMultiSelectTagValue} // Function will trigger on select event
                                labelledBy={"Please Select"}
                                hasSelectAll={true}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6">
                          <div className="form-sub-div">
                            <h6 className="text-bold">
                              Topic
                              {edit && (
                                <span
                                  className="inline-add-link"
                                  onClick={() => openInlineCreate("topic")}
                                >
                                  + New
                                </span>
                              )}
                            </h6>
                            <div className="form-elements">
                              <MultiSelect
                                disabled={!edit}
                                ref={register({ required: true })}
                                options={topicOptions} // Options to display in the dropdown
                                value={multiSelectTopicValue} // Preselected value to persist in dropdown
                                onChange={onTopicChange} // Prunes subtopics no longer under a selected topic
                                labelledBy={"Please Select"}
                                hasSelectAll={true}
                              />
                            </div>
                          </div>
                          <div className="form-sub-div">
                            <h6 className="text-bold">
                              Subtopic
                              {edit && (
                                <span
                                  className="inline-add-link"
                                  onClick={() => openInlineCreate("subtopic")}
                                >
                                  + New
                                </span>
                              )}
                            </h6>
                            <div className="form-elements">
                              <MultiSelect
                                disabled={!edit}
                                options={subtopicOptions} // Filtered by the currently selected topic(s)
                                value={multiSelectSubtopicValue} // Preselected value to persist in dropdown
                                onChange={setMultiSelectSubtopicValue} // Function will trigger on select event
                                labelledBy={"Please Select"}
                                hasSelectAll={true}
                              />
                            </div>
                          </div>
                          <div className="form-sub-div">
                            <h6 className="text-bold">Complexity</h6>
                            <div className="form-elements">
                              <select
                                className="form-details questions"
                                name="questionType"
                                value={joditValues?.questionType}
                                readOnly={!edit}
                                onChange={(e) => {
                                  setJoditValues({
                                    ...joditValues,
                                    questionType: e.target.value,
                                  });
                                }}
                              >
                                <option>EASY</option>
                                <option>MEDIUM</option>
                                <option>HARD</option>
                              </select>
                            </div>
                          </div>
                          <div className="form-sub-div">
                            <h6 className="text-bold">Correct Option</h6>
                            <div className="form-elements">
                              {edit ? (
                                <div>
                                  <div class="">
                                    <MultiSelect
                                      options={answerOptions} // Options to display in the dropdown
                                      value={multiSelectValue} // Preselected value to persist in dropdown
                                      onChange={setMultiSelectValue} // Function will trigger on select event
                                      labelledBy={"Please Select"}
                                      hasSelectAll={false}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <input
                                  className="form-details"
                                  type="text"
                                  value={answers
                                    .filter((answer) => answer.correct)
                                    .map(
                                      (ele, index) =>
                                        "Option" + (ele.ansOrder + 1)
                                    )
                                    .join(", ")}
                                  readOnly
                                />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
                          <div className="form-sub-div">
                            <h6 className="text-bold">Question *</h6>
                            <div className="form-elements">
                              <JoditEditor
                                ref={editor}
                                value={joditValues["question"]}
                                config={
                                  edit
                                    ? QUESTION_JODIT_CONFIG
                                    : QUESTION_JODIT_CONFIG_READONLY
                                }
                                tabIndex={1} // tabIndex of textarea
                                onBlur={(e) => {
                                  setJoditValues({
                                    ...joditValues,
                                    question: e?.target?.innerHTML || e,
                                  });
                                }} // preferred to use only this option to update the content for performance reasons
                                onChange={rememberEditorValue("question")}
                              />

                              <div class="error-msg">
                                {errors?.question &&
                                  displayError("Question is required")}
                              </div>
                            </div>
                          </div>

                          <div className="form-sub-div form-sub-div-nomargin">
                            {edit}
                            {answers.map((ele, ind) => {
                              return (
                                <div
                                  key={"Option" + (ind + 1)}
                                  className="option-sub"
                                >
                                  <h6 className="text-bold">
                                    {"Option" + (ind + 1)}
                                  </h6>
                                  <div className="form-elements">
                                    <JoditEditor
                                      ref={editor}
                                      value={joditValues["option" + (ind + 1)]}
                                      config={
                                        edit
                                          ? RICH_TEXT_JODIT_CONFIG
                                          : RICH_TEXT_JODIT_CONFIG_READONLY
                                      }
                                      tabIndex={1} // tabIndex of textarea
                                      onBlur={(e) => {
                                        // Copy the objects too, not just the array -
                                        // see the deep-copy note on setAnswers(answer).
                                        const ans = answers.map((a) => ({ ...a }));
                                        ans[ind]["answer"] =
                                          e?.target?.innerHTML || e;
                                        setAnswers(ans);
                                      }} // preferred to use only this option to update the content for performance reasons
                                      onChange={rememberEditorValue(
                                        "option" + (ind + 1)
                                      )}
                                    />
                                    <div class="error-msg">
                                      {errors?.["option" + (ind + 1)] &&
                                        displayError(
                                          "Option" + (ind + 1) + " is required"
                                        )}{" "}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          <div className="form-sub-div">
                            <h6 className="text-bold">Explanation</h6>
                            <div className="form-elements">
                              <JoditEditor
                                ref={editor}
                                value={joditValues["explanation"]}
                                config={
                                  edit
                                    ? RICH_TEXT_JODIT_CONFIG
                                    : RICH_TEXT_JODIT_CONFIG_READONLY
                                }
                                tabIndex={1} // tabIndex of textarea
                                onBlur={(e) => {
                                  setJoditValues({
                                    ...joditValues,
                                    explanation: e?.target?.innerHTML || e,
                                  });
                                }} // preferred to use only this option to update the content for performance reasons
                                onChange={rememberEditorValue("explanation")}
                              />
                              <div class="error-msg">
                                {errors?.explanation &&
                                  displayError("Explanation is required")}{" "}
                              </div>
                            </div>
                          </div>

                          <div className="form-sub-div">
                            <h6 className="text-bold">Reference</h6>
                            <div className="form-elements">
                              <JoditEditor
                                ref={editor}
                                value={joditValues["reference"]}
                                config={
                                  edit
                                    ? RICH_TEXT_JODIT_CONFIG
                                    : RICH_TEXT_JODIT_CONFIG_READONLY
                                }
                                tabIndex={1} // tabIndex of textarea
                                onBlur={(e) =>
                                  setJoditValues({
                                    ...joditValues,
                                    reference: e?.target?.innerHTML || e,
                                  })
                                } // preferred to use only this option to update the content for performance reasons
                                onChange={rememberEditorValue("reference")}
                              />
                              <div class="error-msg">
                                {errors?.explanation &&
                                  displayError("Reference is required")}{" "}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {errors?.Topics}
                  </form>
                </div>
              </div>
              <div className="btn-list-div">
                {displyNavs ? (
                  <React.Fragment>
                    <button
                      className="btn-common"
                      onClick={() => onNavClick("prev")}
                      disabled={disablePrev}
                    >
                      Previous
                    </button>{" "}
                    <span style={{ margin: "0 12px" }}>
                      {getQuestionIds().indexOf(+id) +
                        1 +
                        " of " +
                        getQuestionIds().length}
                    </span>
                    <button
                      className="btn-common"
                      disabled={disableNext}
                      onClick={() => onNavClick("next")}
                    >
                      Next
                    </button>
                  </React.Fragment>
                ) : (
                  ""
                )}
              </div>
            </div>
          )}
        </div>
      )}
      <div
        class="modal fade show"
        role="dialog"
        style={{ display: displayComments ? "block" : "" }}
      >
        <div class="modal-dialog modal-dialog-custom">
          <div class="modal-content">
            <div class="modal-header modal-header-custom">
              <button
                type="button"
                class="close"
                data-dismiss="modal"
                onClick={() => {
                  setDisplayComments(false);
                }}
              >
                <div class="close-btn">
                  <i class="fa fa-close" aria-hidden="true"></i>
                </div>
              </button>
            </div>
            <div class="modal-body modal-body-custom">
              {displayComments ? (
                <div class="comments-div">
                  <div class="comments-text-div">Add comments</div>
                  <textarea
                    class="textarea-custom"
                    onChange={($event) => (comments = $event.target.value)}
                    value={comments}
                  ></textarea>
                  <button
                    class="btn-common float-right"
                    onClick={() => onClickCommentsOk()}
                    data-dismiss="modal"
                  >
                    OK
                  </button>
                </div>
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>
      {(!edit || (viewQuestion && +id > 0)) && (
        <QuestionViewer
          whereUsedPopupgridOptions={whereUsedPopupgridOptions}
          feedbackPopupgridOptions={feedbackPopupgridOptions}
          prevQuestion={prevQuestion}
          nxtQuestion={nxtQuestion}
          {...questionData}
          setId={setId}
          count={QBCount}
          showWhereUsedGrid={showWhereUsedGrid}
        />
      )}
      <Dialog
        open={inlineCreate.open}
        onClose={closeInlineCreate}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Add new {inlineCreateLabels[inlineCreate.type] || ""}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            variant="standard"
            margin="dense"
            label={`${inlineCreateLabels[inlineCreate.type] || ""} name`}
            value={inlineCreateValue}
            onChange={(e) => setInlineCreateValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (!inlineCreateSaving) handleInlineCreateSave();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeInlineCreate} disabled={inlineCreateSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleInlineCreateSave}
            disabled={inlineCreateSaving || !inlineCreateValue.trim()}
            variant="contained"
          >
            {inlineCreateSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AddQuestion;
